/**
 * 裝置連線狀態工具函式
 * 統一判斷和顯示裝置連線狀態的邏輯
 */

// 連線狀態類型
export type DeviceConnectionState = 'connected' | 'disconnected' | 'long_disconnected'

// 預設閾值：1 小時
const DEFAULT_LONG_DISCONNECTED_THRESHOLD_MS = 60 * 60 * 1000

interface GetDeviceConnectionStateOptions {
  /** 長時間離線的閾值（毫秒），預設 1 小時 */
  longDisconnectedThresholdMs?: number
}

interface DeviceConnectionStateResult {
  state: DeviceConnectionState
  lastConnectedAt: string | null
}

/**
 * 判斷裝置連線狀態
 *
 * @param isOnline - 從 Redis/WebSocket 取得的即時在線狀態
 * @param lastConnectedAt - 從 DB 取得的最後連線時間（ISO string 或 Date 或 null）
 * @param options - 選項
 * @returns 連線狀態和最後連線時間
 *
 * @example
 * // WebSocket 頁面（有即時狀態）
 * const { state } = getDeviceConnectionState(true, '2024-01-01T12:00:00Z')
 * // state = 'connected'
 *
 * @example
 * // REST API 頁面（無即時狀態）
 * const { state } = getDeviceConnectionState(false, '2024-01-01T12:00:00Z')
 * // state = 'disconnected' 或 'long_disconnected'（取決於時間差）
 */
export function getDeviceConnectionState(
  isOnline: boolean,
  lastConnectedAt: string | Date | null | undefined,
  options?: GetDeviceConnectionStateOptions,
): DeviceConnectionStateResult {
  const thresholdMs = options?.longDisconnectedThresholdMs ?? DEFAULT_LONG_DISCONNECTED_THRESHOLD_MS

  // 如果在線，直接返回 connected
  if (isOnline) {
    return { state: 'connected', lastConnectedAt: null }
  }

  // 處理 lastConnectedAt
  const lastConnectedAtStr = normalizeLastConnectedAt(lastConnectedAt)

  // 沒有連線記錄，視為長時間離線
  if (!lastConnectedAtStr) {
    return { state: 'long_disconnected', lastConnectedAt: null }
  }

  // 計算距離上次連線的時間
  const lastConnectedTime = new Date(lastConnectedAtStr).getTime()
  const now = Date.now()
  const timeSinceLastConnection = now - lastConnectedTime

  // 超過閾值，視為長時間離線
  if (timeSinceLastConnection >= thresholdMs) {
    return { state: 'long_disconnected', lastConnectedAt: lastConnectedAtStr }
  }

  return { state: 'disconnected', lastConnectedAt: lastConnectedAtStr }
}

/**
 * 將 lastConnectedAt 正規化為 ISO string 或 null
 */
function normalizeLastConnectedAt(
  value: string | Date | null | undefined,
): string | null {
  if (!value) return null

  if (value instanceof Date) {
    return value.toISOString()
  }

  // 驗證是否為有效的日期字串
  const date = new Date(value)
  if (isNaN(date.getTime())) {
    return null
  }

  return value
}

interface ConnectionDisplayInfo {
  /** 圓點的 CSS class */
  dotClass: string
  /** 顯示文字 */
  text: string
  /** 文字的 CSS class */
  textClass: string
  /** 是否顯示警示圖示 */
  showWarning: boolean
}

/**
 * 根據連線狀態取得顯示資訊
 *
 * @param state - 連線狀態
 * @returns 顯示資訊（顏色、文字等）
 */
export function getConnectionDisplay(state: DeviceConnectionState): ConnectionDisplayInfo {
  switch (state) {
    case 'connected':
      return {
        dotClass: 'bg-green-500',
        text: '已連線',
        textClass: 'text-green-600',
        showWarning: false,
      }
    case 'disconnected':
      return {
        dotClass: 'bg-red-500',
        text: '未連線',
        textClass: 'text-red-600',
        showWarning: false,
      }
    case 'long_disconnected':
      return {
        dotClass: 'bg-orange-500',
        text: '離線超過1小時',
        textClass: 'text-orange-600',
        showWarning: true,
      }
  }
}

/**
 * 格式化最後連線時間的顯示
 *
 * @param lastConnectedAt - 最後連線時間（ISO string）
 * @returns 格式化後的時間字串
 */
export function formatLastConnectedAt(lastConnectedAt: string | null): string {
  if (!lastConnectedAt) {
    return '無連線記錄'
  }

  // 直接顯示 UTC 值（與全站其他時間欄位一致），避免瀏覽器時區轉換造成前後不一致
  const date = new Date(lastConnectedAt)
  return `${date.getUTCMonth() + 1}/${date.getUTCDate()} ${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}`
}
